import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useStore from '@/store/useStore';
import { toast } from '@/components/ui/use-toast';
import { AlertCircle, Calendar } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { fetchUmpires, insertMatchUmpires } from '@/services/supabaseService';
import { supabase } from '@/integrations/supabase/client';

const matchTypes = [
  { id: "t10", name: "T10", overs: 10, balls: 6, innings: 2 },
  { id: "t10i", name: "T10I", overs: 10, balls: 6, innings: 2 },
  { id: "t20", name: "T20", overs: 20, balls: 6, innings: 2 },
  { id: "t20i", name: "T20I", overs: 20, balls: 6, innings: 2 },
  { id: "odi", name: "ODI", overs: 50, balls: 6, innings: 2 },
  { id: "test", name: "Test", overs: 90, balls: 6, innings: 4 },
  { id: "first-class", name: "First-Class", overs: 90, balls: 6, innings: 4 },
  { id: "custom", name: "Custom", overs: 20, balls: 6, innings: 2 }
];

const umpireOptions = [
  { id: 'ump1', name: 'Aleem Dar' },
  { id: 'ump2', name: 'Kumar Dharmasena' },
  { id: 'ump3', name: 'Richard Kettleborough' },
  { id: 'ump4', name: 'Javed Malik' },
  { id: 'ump5', name: 'Marais Erasmus' },
  { id: 'ump6', name: 'Nitin Menon' },
  { id: 'ump7', name: 'Paul Reiffel' },
  { id: 'ump8', name: 'Chris Gaffaney' },
  { id: 'ump9', name: 'Rod Tucker' },
  { id: 'ump10', name: 'Michael Gough' },
];

const NewMatchPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { teams, tournaments, players, fetchTeams, fetchTournaments, fetchPlayers, createMatch } = useStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [tournamentId, setTournamentId] = useState('');
  const [team1Id, setTeam1Id] = useState('');
  const [team2Id, setTeam2Id] = useState('');
  const [date, setDate] = useState('');
  const [city, setCity] = useState('');
  const [groundName, setGroundName] = useState('');
  const [matchType, setMatchType] = useState('t20');
  const [customMatchType, setCustomMatchType] = useState('');
  const [totalOvers, setTotalOvers] = useState(20);
  const [ballsPerOver, setBallsPerOver] = useState(6);
  const [totalWickets, setTotalWickets] = useState(10);
  const [totalInnings, setTotalInnings] = useState(2);
  
  // Team captain selection
  const [team1CaptainId, setTeam1CaptainId] = useState('');
  const [team2CaptainId, setTeam2CaptainId] = useState('');

  // Team vice-captain selection
  const [team1ViceCaptainId, setTeam1ViceCaptainId] = useState('');
  const [team2ViceCaptainId, setTeam2ViceCaptainId] = useState('');
  
  // Add umpire state inside the component
  const [onFieldUmpire1, setOnFieldUmpire1] = useState('');
  const [onFieldUmpire2, setOnFieldUmpire2] = useState('');
  const [thirdUmpire, setThirdUmpire] = useState('');
  const [fourthUmpire, setFourthUmpire] = useState('');
  
  // Add state for collapse
  const [showUmpires, setShowUmpires] = useState(false);
  
  const [umpires, setUmpires] = useState<any[]>([]);
  
  // Add this at the top of the component
  const dateInputRef = useRef<HTMLInputElement>(null);
  
  // Fetch teams, tournaments, players, and umpires when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchTeams(), fetchTournaments(), fetchPlayers()]);
        const umpireList = await fetchUmpires();
        setUmpires(umpireList);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [fetchTeams, fetchTournaments, fetchPlayers]);
  
  // Reset captains when team changes
  useEffect(() => {
    setTeam1CaptainId('');
    setTeam1ViceCaptainId('');
  }, [team1Id]);

  useEffect(() => {
    setTeam2CaptainId('');
    setTeam2ViceCaptainId('');
  }, [team2Id]);

  // Get team players
  const team1Players = players.filter(player => player.teamIds && player.teamIds.includes(team1Id));
  const team2Players = players.filter(player => player.teamIds && player.teamIds.includes(team2Id));
  
  // Filter out selected captain/vice-captain from the other's dropdown
  const team1CaptainOptions = team1Players.filter(p => !team1ViceCaptainId || p.id !== team1ViceCaptainId);
  const team1ViceCaptainOptions = team1Players.filter(p => !team1CaptainId || p.id !== team1CaptainId);
  const team2CaptainOptions = team2Players.filter(p => !team2ViceCaptainId || p.id !== team2ViceCaptainId);
  const team2ViceCaptainOptions = team2Players.filter(p => !team2CaptainId || p.id !== team2CaptainId);

  const hasEnoughPlayers = team1Players.length >= 2 && team2Players.length >= 1;
  
  // Set match type
  useEffect(() => {
    if (matchType !== 'custom') {
      const selectedType = matchTypes.find(type => type.id === matchType);
      if (selectedType) {
        setTotalOvers(selectedType.overs);
        setBallsPerOver(selectedType.balls);
        setTotalInnings(selectedType.innings);
      }
    }
  }, [matchType]);
  
  // Restore form state if coming back from Add Umpire
  React.useEffect(() => {
    if (location.state && location.state.fromAddUmpire) {
      const s = location.state.formState;
      if (s) {
        setTournamentId(s.tournamentId || '');
        setTeam1Id(s.team1Id || '');
        setTeam2Id(s.team2Id || '');
        setDate(s.date || '');
        setCity(s.city || '');
        setGroundName(s.groundName || '');
        setMatchType(s.matchType || 't20');
        setCustomMatchType(s.customMatchType || '');
        setTotalOvers(s.totalOvers || 20);
        setBallsPerOver(s.ballsPerOver || 6);
        setTotalWickets(s.totalWickets || 10);
        setTotalInnings(s.totalInnings || 2);
        setTeam1CaptainId(s.team1CaptainId || '');
        setTeam2CaptainId(s.team2CaptainId || '');
        setTeam1ViceCaptainId(s.team1ViceCaptainId || '');
        setTeam2ViceCaptainId(s.team2ViceCaptainId || '');
        setOnFieldUmpire1(s.onFieldUmpire1 || '');
        setOnFieldUmpire2(s.onFieldUmpire2 || '');
        setThirdUmpire(s.thirdUmpire || '');
        setFourthUmpire(s.fourthUmpire || '');
        setShowUmpires(s.showUmpires || false);
      }
    }
  }, [location.state]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tournamentId || !team1Id || !team2Id || !date || !city) {
      toast({
        title: "Missing information",
        description: "Please fill out all required fields.",
        variant: "destructive",
      });
      return;
    }
    
    if (!date || isNaN(new Date(date).getTime())) {
      toast({
        title: "Invalid date",
        description: "Please select a valid match date.",
        variant: "destructive",
      });
      return;
    }
    
    if (team1Id === team2Id) {
      toast({
        title: "Invalid teams",
        description: "Teams cannot be the same",
        variant: "destructive",
      });
      return;
    }
    
    if (!hasEnoughPlayers) {
      toast({
        title: "Insufficient players",
        description: "Each team needs at least 2 players for batting and 1 player for bowling.",
        variant: "destructive",
      });
      return;
    }
    
    if (team1CaptainId && team1CaptainId === team1ViceCaptainId) {
      toast({
        title: "Invalid selection",
        description: "Team 1 captain and vice-captain cannot be the same person.",
        variant: "destructive",
      });
      return;
    }
    
    if (team2CaptainId && team2CaptainId === team2ViceCaptainId) {
      toast({
        title: "Invalid selection",
        description: "Team 2 captain and vice-captain cannot be the same person.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const matchData = {
        id: '', // Will be generated by the database
        tournamentId,
        team1Id,
        team2Id,
        matchDate: new Date(date).toISOString(),
        city,
        groundName,
        status: 'upcoming' as const,
        team1Score: { runs: 0, wickets: 0, overs: 0, balls: 0 },
        team2Score: { runs: 0, wickets: 0, overs: 0, balls: 0 },
        currentInnings: 1 as const,
        currentBattingTeam: team1Id,
        matchType: matchType === 'custom' ? customMatchType : matchType,
        totalOvers,
        ballsPerOver,
        totalWickets,
        wicketsPerInnings: totalWickets,
        totalInnings,
        team1CaptainId: team1CaptainId || undefined,
        team2CaptainId: team2CaptainId || undefined,
        team1ViceCaptainId: team1ViceCaptainId || undefined,
        team2ViceCaptainId: team2ViceCaptainId || undefined,
        onFieldUmpire1: onFieldUmpire1 || undefined,
        onFieldUmpire2: onFieldUmpire2 || undefined,
        thirdUmpire: thirdUmpire || undefined,
        fourthUmpire: fourthUmpire || undefined,
      };
      
      const newMatch = await createMatch(matchData);
      // Fetch userId from Supabase session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error('User not authenticated');
      // Prepare umpire assignments
      const umpireAssignments = [];
      if (onFieldUmpire1) umpireAssignments.push({ umpire_id: onFieldUmpire1, role: 'On-field 1', user_id: userId });
      if (onFieldUmpire2) umpireAssignments.push({ umpire_id: onFieldUmpire2, role: 'On-field 2', user_id: userId });
      if (thirdUmpire) umpireAssignments.push({ umpire_id: thirdUmpire, role: 'Third', user_id: userId });
      if (fourthUmpire) umpireAssignments.push({ umpire_id: fourthUmpire, role: 'Fourth', user_id: userId });
      if (umpireAssignments.length > 0) {
        await insertMatchUmpires(newMatch.id, umpireAssignments);
      }
      navigate('/matches');
    } catch (error) {
      console.error('Error scheduling match:', error);
      toast({
        title: "Error",
        description: "Failed to schedule match.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Validation checks for adequate teams and players
  const hasTeams = teams.length >= 2;
  
  if (!isLoading && !hasTeams) {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/matches")} 
          className="mb-4"
        >
          ← Back to Matches
        </Button>
        
        <Card>
          <CardHeader>
            <CardTitle>Schedule New Match</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Cannot schedule a match yet</AlertTitle>
              <AlertDescription>
                You need at least two teams before you can schedule a match.
              </AlertDescription>
            </Alert>
            
            <div className="flex justify-center mt-4">
              <Button 
                onClick={() => navigate("/teams/new")}
                className="bg-cricket-green"
              >
                Create Teams First
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <Button 
        variant="ghost" 
        onClick={() => navigate("/matches")} 
        className="mb-4"
      >
        ← Back to Matches
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle>Schedule New Match</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-6">Loading data...</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="tournament">Tournament <span className="text-red-500">*</span></Label>
                <Select 
                  onValueChange={setTournamentId} 
                  required
                >
                  <SelectTrigger id="tournament">
                    <SelectValue placeholder="Select a tournament" />
                  </SelectTrigger>
                  <SelectContent>
                    {tournaments.map(tournament => (
                      <SelectItem key={tournament.id} value={tournament.id}>
                        {tournament.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="team1">Team 1 <span className="text-red-500">*</span></Label>
                  <Select 
                    onValueChange={setTeam1Id} 
                    required
                  >
                    <SelectTrigger id="team1">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map(team => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name} {team.shortCode ? `(${team.shortCode})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {team1Id && team1Players.length < 2 && (
                    <p className="text-red-500 text-xs mt-1">
                      This team needs at least 2 players.
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="team2">Team 2 <span className="text-red-500">*</span></Label>
                  <Select 
                    onValueChange={setTeam2Id} 
                    required
                    disabled={!team1Id}
                  >
                    <SelectTrigger id="team2">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.filter(team => team.id !== team1Id).map(team => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name} {team.shortCode ? `(${team.shortCode})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {team2Id && team2Players.length < 1 && (
                    <p className="text-red-500 text-xs mt-1">
                      This team needs at least 1 player.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="team1-captain">Team 1 Captain</Label>
                  <Select onValueChange={setTeam1CaptainId} value={team1CaptainId}>
                    <SelectTrigger id="team1-captain">
                      <SelectValue placeholder="Select team captain" />
                    </SelectTrigger>
                    <SelectContent>
                      {team1CaptainOptions.map(player => (
                        <SelectItem key={player.id} value={player.id}>
                          {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="team2-captain">Team 2 Captain</Label>
                  <Select onValueChange={setTeam2CaptainId} value={team2CaptainId}>
                    <SelectTrigger id="team2-captain">
                      <SelectValue placeholder="Select team captain" />
                    </SelectTrigger>
                    <SelectContent>
                      {team2CaptainOptions.map(player => (
                        <SelectItem key={player.id} value={player.id}>
                          {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="team1-vice-captain">Team 1 Vice Captain</Label>
                  <Select onValueChange={setTeam1ViceCaptainId} value={team1ViceCaptainId}>
                    <SelectTrigger id="team1-vice-captain">
                      <SelectValue placeholder="Select team vice-captain" />
                    </SelectTrigger>
                    <SelectContent>
                      {team1ViceCaptainOptions.map(player => (
                        <SelectItem key={player.id} value={player.id}>
                          {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="team2-vice-captain">Team 2 Vice Captain</Label>
                  <Select onValueChange={setTeam2ViceCaptainId} value={team2ViceCaptainId}>
                    <SelectTrigger id="team2-vice-captain">
                      <SelectValue placeholder="Select team vice-captain" />
                    </SelectTrigger>
                    <SelectContent>
                      {team2ViceCaptainOptions.map(player => (
                        <SelectItem key={player.id} value={player.id}>
                          {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className={`grid grid-cols-1 md:grid-cols-${matchType === 'custom' ? '3' : '2'} gap-4`}>
                <div>
                  <Label htmlFor="matchType" className="mb-0">
                    Match Type <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    onValueChange={setMatchType}
                    value={matchType}
                    required
                  >
                    <SelectTrigger id="matchType">
                      <SelectValue placeholder="Select match type" />
                    </SelectTrigger>
                    <SelectContent>
                      {matchTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {matchType === 'custom' && (
                  <div>
                    <Label htmlFor="customMatchType" className="mb-0">
                      Custom Match Name</Label>
                    <Input
                      id="customMatchType"
                      placeholder="Super 6"
                      value={customMatchType}
                      onChange={(e) => setCustomMatchType(e.target.value)}
                      className="mb-0"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="totalInnings" className="mb-0">
                    Total Innings
                  </Label>
                  <Input 
                    id="totalInnings" 
                    type="number" 
                    value={totalInnings}
                    onChange={(e) => setTotalInnings(parseInt(e.target.value))}
                    min="1"
                    max="4"
                    disabled={matchType !== 'custom'}
                    required
                  />
                </div>
              </div>

              {/* New fields for Balls per Over, Overs, and Wickets per Innings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ballsPerOver">Balls per Over</Label>
                  <Input
                    id="ballsPerOver"
                    type="number"
                    min="1"
                    max="10"
                    value={ballsPerOver}
                    onChange={e => setBallsPerOver(Number(e.target.value))}
                    required
                    disabled={matchType !== 'custom'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalOvers">Overs per Innings</Label>
                  <Input
                    id="totalOvers"
                    type="number"
                    min="1"
                    value={totalOvers}
                    onChange={e => setTotalOvers(Number(e.target.value))}
                    required
                    disabled={matchType !== 'custom'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalWickets">Wickets per Innings</Label>
                  <Input
                    id="totalWickets"
                    type="number"
                    min="1"
                    max="20"
                    value={totalWickets}
                    onChange={e => setTotalWickets(Number(e.target.value))}
                    required
                    disabled={matchType !== 'custom'}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date & Time <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Input 
                      ref={dateInputRef}
                      id="date" 
                      type="datetime-local" 
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <span
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400 cursor-pointer"
                      onClick={() => dateInputRef.current?.showPicker && dateInputRef.current.showPicker()}
                      tabIndex={-1}
                    >
                      <Calendar className="w-5 h-5" />
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City <span className="text-red-500">*</span></Label>
                  <Input 
                    id="city" 
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter match city" 
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="groundName">Ground</Label>
                  <Input 
                    id="groundName" 
                    value={groundName}
                    onChange={(e) => setGroundName(e.target.value)}
                    placeholder="Enter ground name" 
                  />
                </div>
              </div>
              
              {!hasEnoughPlayers && team1Id && team2Id && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Not enough players</AlertTitle>
                  <AlertDescription>
                    {team1Players.length < 2 && (
                      <p>Team 1 ({teams.find(t => t.id === team1Id)?.name}) needs at least 2 players.</p>
                    )}
                    {team2Players.length < 1 && (
                      <p>Team 2 ({teams.find(t => t.id === team2Id)?.name}) needs at least 1 player.</p>
                    )}
                    <div className="mt-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate("/players/new")}
                      >
                        Add Players
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
              
              <hr className="my-6" />
              <div className="space-y-2 mt-6">
                <button
                  type="button"
                  className="w-full flex items-center font-semibold text-left group focus:outline-none cursor-pointer"
                  onClick={() => setShowUmpires((prev) => !prev)}
                  aria-expanded={showUmpires}
                  aria-controls="umpire-fields"
                  style={{ background: 'none', border: 'none', padding: 0 }}
                >
                  <span className="pr-2">Umpires(optional)</span>
                  <hr className="flex-1 border-t border-muted mx-2" />
                  {showUmpires && (
                    <Button variant="link" type="button" className="text-sm px-0 mr-2" onClick={e => { e.stopPropagation(); const formState = { tournamentId, team1Id, team2Id, date, city, matchType, customMatchType, totalOvers, ballsPerOver, totalWickets, totalInnings, team1CaptainId, team2CaptainId, team1ViceCaptainId, team2ViceCaptainId, onFieldUmpire1, onFieldUmpire2, thirdUmpire, fourthUmpire, showUmpires, }; navigate('/umpires/new?from=/matches/new', { state: { fromAddUmpire: true, formState } }); }}>
                      Add New Umpire
                    </Button>
                  )}
                  <svg
                    className="h-4 w-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {showUmpires ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    )}
                  </svg>
                </button>
                {showUmpires && (
                  <div id="umpire-fields" className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div className="space-y-1">
                      <Label htmlFor="onFieldUmpire1">On-field Umpire 1<br /><span className="text-xs text-muted-foreground">(Bowler's End)</span></Label>
                      <Select value={onFieldUmpire1} onValueChange={setOnFieldUmpire1}>
                        <SelectTrigger id="onFieldUmpire1">
                          <SelectValue placeholder="Select umpire" />
                        </SelectTrigger>
                        <SelectContent>
                          {umpires.map(u => (
                            <SelectItem key={u.umpireId} value={u.umpireId}>{u.fullName || (u.firstName + ' ' + u.lastName)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="onFieldUmpire2">On-field Umpire 2<br /><span className="text-xs text-muted-foreground">(Square Leg)</span></Label>
                      <Select value={onFieldUmpire2} onValueChange={setOnFieldUmpire2}>
                        <SelectTrigger id="onFieldUmpire2">
                          <SelectValue placeholder="Select umpire" />
                        </SelectTrigger>
                        <SelectContent>
                          {umpires.map(u => (
                            <SelectItem key={u.umpireId} value={u.umpireId}>{u.fullName || (u.firstName + ' ' + u.lastName)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="thirdUmpire">Third Umpire<br /><span className="text-xs text-muted-foreground">(TV)</span></Label>
                      <Select value={thirdUmpire} onValueChange={setThirdUmpire}>
                        <SelectTrigger id="thirdUmpire">
                          <SelectValue placeholder="Select umpire" />
                        </SelectTrigger>
                        <SelectContent>
                          {umpires.map(u => (
                            <SelectItem key={u.umpireId} value={u.umpireId}>{u.fullName || (u.firstName + ' ' + u.lastName)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="fourthUmpire">Fourth Umpire<br /><span className="text-xs text-muted-foreground">(Logistics)</span></Label>
                      <Select value={fourthUmpire} onValueChange={setFourthUmpire}>
                        <SelectTrigger id="fourthUmpire">
                          <SelectValue placeholder="Select umpire" />
                        </SelectTrigger>
                        <SelectContent>
                          {umpires.map(u => (
                            <SelectItem key={u.umpireId} value={u.umpireId}>{u.fullName || (u.firstName + ' ' + u.lastName)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="bg-cricket-green w-full" 
                disabled={isSubmitting || !hasEnoughPlayers}
              >
                {isSubmitting ? 'Scheduling...' : 'Schedule Match'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewMatchPage;
